import { useCurrentWorkspace } from '@gripp/shared-logic';
import {
  Button,
  ColumnData,
  EditAlertForm,
  Icon,
  NewAlertForm,
  SlideAction,
  SlideLayout,
  SlideLayoutLocationState,
  tokens,
  ViewMode,
} from '@gripp/shared-ui';
import { MutableRefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { RoutinePreviewColumn } from '../../new-routines/components/routinePreviewColumn';
import { ProductAlertList } from './productAlertList';
import { ProductNewAlertHeaderAction } from './productNewAlertHeaderAction';

type FormRef = {
  handleSubmit: (fn: (data: unknown) => Promise<void>) => () => void;
  reset: () => void;
} | null;

type AlertFormLocationState = SlideLayoutLocationState & {
  fromRoutine?: boolean;
} & Record<string, unknown>;

const toLocationState = (state: unknown): AlertFormLocationState | null =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as AlertFormLocationState)
    : null;

// ─── Form header (right column headerComponent) ──────────────────────────────

const AlertFormHeader = ({
  formRef,
}: {
  formRef: MutableRefObject<FormRef>;
}) => (
  <Pressable
    onPress={() => formRef.current?.reset()}
    style={formStyles.resetButton}
  >
    <Icon name="Reset" size={20} color={tokens.colors.brand.black} />
  </Pressable>
);

// ─── Form content (right column) ─────────────────────────────────────────────

const AlertFormContent = ({
  productId,
  routineId,
  alertId,
  onBack,
  formRef,
  onRegisterBackGuard,
}: {
  productId: string;
  routineId: string;
  alertId?: string;
  onBack: () => void;
  formRef: MutableRefObject<FormRef>;
  onRegisterBackGuard: (
    guard: (onProceed: () => void, onCancel?: () => void) => void
  ) => void;
}) => {
  const currentWorkspace = useCurrentWorkspace();
  const { t } = useTranslation();
  const isEdit = !!alertId;

  const confirmBottomComponent = (onSubmit: () => void, disabled: boolean) => (
    <View>
      <Button
        variant="rectangular"
        size="large"
        state={disabled ? 'default' : 'active'}
        disabled={disabled}
        onPress={() => {
          if (!disabled) {
            onSubmit();
          }
        }}
        style={formStyles.confirmButton}
      >
        {t('alerts.confirm')}
      </Button>
    </View>
  );

  return (
    <View style={formStyles.container}>
      {isEdit ? (
        <EditAlertForm
          key={alertId}
          alertId={alertId}
          routineId={routineId}
          productId={productId}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={confirmBottomComponent}
          onRegisterBackGuard={onRegisterBackGuard}
        />
      ) : (
        <NewAlertForm
          routineId={routineId}
          workspaceAssetId=""
          productId={productId}
          workspace={currentWorkspace?.id}
          onClose={onBack}
          formRef={formRef}
          renderBottomComponent={confirmBottomComponent}
          onRegisterBackGuard={onRegisterBackGuard}
        />
      )}
    </View>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const ProductAlertFormPage = () => {
  const { productId = '', routineId = '', alertId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef<FormRef>(null);
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  if (!productId || !routineId) return null;

  const routineUrl = `/products/${productId}/routines/${routineId}`;
  const alertsUrl = `/products/${productId}/alerts/${routineId}`;
  const locationState = toLocationState(location.state);
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  const fromRoutine = locationState?.fromRoutine;
  const {
    fromRoutine: _fromRoutine,
    slideLayoutInitialTransition: _slideLayoutInitialTransition,
    ...backState
  } = locationState ?? {};

  const getStateWithTransition = (transition: SlideAction) => ({
    ...backState,
    slideLayoutInitialTransition: transition,
  });

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const leftColumn: ColumnData = fromRoutine
    ? {
        title: 'product.detail.routines',
        mainComponent: (
          <RoutinePreviewColumn routineId={routineId} basePath={routineUrl} />
        ),
        showBackButton: true,
        backButtonTransition: SlideAction.PREV,
        backButtonTransitionBehavior: 'concurrent',
        backButtonTransitionExitColumn: 'both',
        onBackButtonPress: () =>
          navigate(routineUrl, {
            state: getStateWithTransition(SlideAction.PREV),
          }),
        disableScroll: true,
      }
    : {
        title: 'alerts.alerts',
        mainComponent: (
          <ProductAlertList
            productId={productId}
            routineId={routineId}
            onAlertSelected={(alert) => {
              if (!alert?.id) return;
              navigate(
                `/products/${productId}/alerts/${routineId}/${alert.id}`
              );
            }}
          />
        ),
        headerComponent: (
          <ProductNewAlertHeaderAction
            productId={productId}
            routineId={routineId}
          />
        ),
        showBackButton: true,
        backButtonTransition: SlideAction.PREV,
        backButtonTransitionBehavior: 'concurrent',
        backButtonTransitionExitColumn: 'both',
        onBackButtonPress: () =>
          navigate(routineUrl, {
            state: getStateWithTransition(SlideAction.PREV),
          }),
        disableScroll: true,
      };

  const rightColumn: ColumnData = {
    title: alertId ? 'alerts.editAlert' : 'alerts.newAlert',
    mainComponent: (
      <AlertFormContent
        productId={productId}
        routineId={routineId}
        alertId={alertId}
        onBack={() =>
          navigate(fromRoutine ? routineUrl : alertsUrl, {
            state: { ...backState },
          })
        }
        formRef={formRef}
        onRegisterBackGuard={(guard) => {
          backGuardRef.current = guard;
        }}
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent',
    backButtonGuard,
    onBackButtonPress: () =>
      navigate(fromRoutine ? routineUrl : alertsUrl, {
        state: getStateWithTransition(SlideAction.PREV),
      }),
    headerComponent: <AlertFormHeader formRef={formRef} />,
    disableScroll: true,
  };

  return (
    <SlideLayout
      leftColumn={leftColumn}
      rightColumn={rightColumn}
      viewMode={ViewMode.DUAL}
      initialTransition={initialTransition}
    />
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const formStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  resetButton: {
    padding: tokens.spacing.xs,
  },
  confirmButton: {
    marginHorizontal: tokens.spacing.lg,
    marginBottom: tokens.spacing.lg,
  },
});
